import { createColumnHelper } from '@tanstack/react-table';
import cn from 'classnames';
import { useIntl } from 'react-intl';

import { Patient } from '@/shared/api/protocol-ts/model/dto_patient_pb';
import { PatientsListColumnName } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';
import { parseDateAsLocal } from '@/shared/lib';

import { organizationModel } from '@/entities/organization';
import { StudiesCounters } from '@/entities/patient';

import { ColumnValues } from '../config/i18n';
import { PatientDoctorsCell } from '../ui/PatientDoctorsCell/PatientDoctorsCell';
import styles from '../ui/PatientList/PatientList.module.scss';
import { PatientNameCell } from '../ui/PatientNameCell/PatientNameCell';

interface ColumnMeta {
  isVisible?: boolean;
}

export const useGetPatientsColumnsScheme = () => {
  const { formatMessage, formatDate } = useIntl();

  const columnHelper = createColumnHelper<Patient>();

  const { isFDA } = useAppSelector(
    organizationModel.selectors.selectFDAEnvironment,
  );

  const columnsScheme = [
    columnHelper.accessor((row) => row, {
      id: PatientsListColumnName.PatientName,
      enableSorting: true,
      minSize: 180,
      header: formatMessage(ColumnValues[PatientsListColumnName.PatientName]),
      cell: (cell) => <PatientNameCell patient={cell.getValue()} />,
    }),
    columnHelper.accessor((row) => row.PersonalData?.DateOfBirth, {
      id: PatientsListColumnName.DateOfBirth,
      enableSorting: false,
      minSize: 50,
      header: formatMessage(ColumnValues[PatientsListColumnName.DateOfBirth]),
      cell: (cell) => {
        const dateOfBirth = cell.getValue();
        const formattedDate = dateOfBirth
          ? formatDate(parseDateAsLocal(dateOfBirth), {
              dateStyle: 'long',
            })
          : '';

        return (
          <div className={cn(styles.infoInCell, styles.greyText)}>
            <span data-ph-mask className={styles.forWhiteSpace}>
              {formattedDate}
            </span>
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.ID, {
      id: PatientsListColumnName.Doctors,
      enableSorting: false,
      minSize: 50,
      header: formatMessage(ColumnValues[PatientsListColumnName.Doctors]),
      cell: (cell) => {
        const patientID = cell.getValue();

        return <PatientDoctorsCell patientID={patientID} />;
      },
    }),
    columnHelper.accessor((row) => row.Revision?.UpdatedAt, {
      id: PatientsListColumnName.LastModified,
      enableSorting: true,
      minSize: 50,
      header: formatMessage(ColumnValues[PatientsListColumnName.LastModified]),
      cell: (cell) => {
        const timestamp = cell.getValue();

        if (!timestamp) {
          return null;
        }

        const formattedDate = formatDate(timestamp.toDate(), {
          dateStyle: 'long',
        });

        return (
          <div className={cn(styles.infoInCell, styles.greyText)}>
            <span className={styles.forWhiteSpace}>{formattedDate}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.ID, {
      id: PatientsListColumnName.Studies,
      meta: { isVisible: !isFDA } as ColumnMeta,
      enableSorting: false,
      minSize: 200,
      header: formatMessage(ColumnValues[PatientsListColumnName.Studies]),
      cell: (cell) => {
        const patientID = cell.getValue();

        return (
          <div>
            <StudiesCounters patientID={patientID} />
          </div>
        );
      },
    }),
  ].filter(
    (column): column is typeof column & { meta: ColumnMeta } =>
      (column.meta as ColumnMeta | undefined)?.isVisible !== false,
  );

  return columnsScheme;
};
